import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  buildWeddingSlug,
  coupleShortName,
  LABEL_PREFIX,
  labelNameForSlug,
  WEDDING_STATUSES,
  weddingLabelName,
  type WeddingStatus,
} from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import WeddingFormFields, { type WeddingFormState } from '@/components/WeddingFormFields';
import CouplePhotoField from '@/components/CouplePhotoField';
import { WEDDING_STATUS_LABELS } from '@/pages/WeddingsPage';

/**
 * Editing and hiding a wedding (PRD §F2).
 *
 * Two things this dialog is careful about, and they are the reason it is not
 * just a copy of `NewWeddingDialog`:
 *
 *  - **The slug is immutable once the first inquiry is sent.** The backend
 *    already enforces that, but silently ignoring a rename is worse than
 *    refusing one: the planner corrects a typo, the dialog says "Saved", and
 *    the Gmail label still reads the old name. So the rule is stated on screen
 *    at the moment it applies, and only then.
 *  - **Delete is not delete.** It hides the wedding from the app. It does not
 *    touch a single message in the planner's mailbox, and the confirm says so
 *    in those words rather than the usual "this cannot be undone".
 *
 * The confirm replaces the form inside the same dialog rather than opening a
 * second one on top of it — one destructive decision on screen at a time.
 */

export interface EditableWedding {
  id: string;
  slug: string;
  /** The label the planner picked at creation, or null/absent to follow the slug. */
  gmailLabelName?: string | null;
  partner1FirstName: string;
  partner1LastName: string | null;
  partner2FirstName: string;
  partner2LastName: string | null;
  weddingDate: string | null;
  startTime: string | null;
  endTime: string | null;
  venueName: string | null;
  venuePlaceId: string | null;
  venueAddress: string | null;
  guestCountEstimate: number | null;
  budgetEstimate: number | null;
  status: string;
  /** R2 object key, or null. Only ever tested for presence here — the key
   *  itself is the server's business. */
  photoKey: string | null;
  /** Used only to decide whether the slug is still free to change. */
  board: { inquiries: { sentAt: string | null }[] }[];
}

/** Postgres renders a `time` as `16:00:00`, and a time input given those eight
 *  characters displays nothing at all rather than complaining. Five characters
 *  is the whole fix. (`weddingDate` needs no equivalent: a `date` column comes
 *  back as `2027-06-12`, which is already what a date input wants.) */
function toTimeInput(raw: string | null): string {
  return raw ? raw.slice(0, 5) : '';
}

/** Seeded on mount and again on every reopen, so the two stay in step. */
function formFrom(w: EditableWedding): WeddingFormState & { status: WeddingStatus } {
  return {
    partner1FirstName: w.partner1FirstName,
    partner1LastName: w.partner1LastName ?? '',
    partner2FirstName: w.partner2FirstName,
    partner2LastName: w.partner2LastName ?? '',
    weddingDate: w.weddingDate ?? '',
    startTime: toTimeInput(w.startTime),
    endTime: toTimeInput(w.endTime),
    venueName: w.venueName ?? '',
    venuePlaceId: w.venuePlaceId,
    venueAddress: w.venueAddress,
    guestCountEstimate: w.guestCountEstimate?.toString() ?? '',
    budgetEstimate: w.budgetEstimate?.toString() ?? '',
    status: w.status as WeddingStatus,
  };
}

export default function EditWeddingDialog({
  wedding,
  open,
  onOpenChange,
}: {
  wedding: EditableWedding;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [form, setForm] = useState(() => formFrom(wedding));

  // Reopening after a cancel must not show the half-typed edits from last time.
  useEffect(() => {
    if (!open) return;
    setConfirmingDelete(false);
    setForm(formFrom(wedding));
  }, [open, wedding]);

  /** Any inquiry already out the door means the label exists in Gmail. */
  const labelIsLive = wedding.board.some((row) => row.inquiries.some((inq) => inq.sentAt));

  const save = useMutation({
    mutationFn: async () => {
      const res = await client.api.weddings[':id'].$put({
        param: { id: wedding.id },
        json: {
          partner1FirstName: form.partner1FirstName.trim(),
          partner1LastName: form.partner1LastName.trim() || null,
          partner2FirstName: form.partner2FirstName.trim(),
          partner2LastName: form.partner2LastName.trim() || null,
          weddingDate: form.weddingDate || null,
          // A cleared time input reads back as `''`, which the schema's
          // 24-hour regex rejects. On the wire, "not set" is null.
          startTime: form.startTime || null,
          endTime: form.endTime || null,
          venueName: form.venueName.trim() || null,
          venuePlaceId: form.venuePlaceId,
          venueAddress: form.venueAddress,
          guestCountEstimate: form.guestCountEstimate ? Number(form.guestCountEstimate) : null,
          budgetEstimate: form.budgetEstimate ? Number(form.budgetEstimate) : null,
          status: form.status,
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to save this wedding');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wedding', wedding.id] });
      queryClient.invalidateQueries({ queryKey: ['weddings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      // Every clock time on the day-of timeline is an offset from the start time
      // this dialog writes, and the panel that shows them is still on screen
      // behind it — including in the state that sent the planner here, which
      // asks for exactly these hours and must not keep asking once they exist.
      queryClient.invalidateQueries({ queryKey: ['wedding-timeline', wedding.id] });
      onOpenChange(false);
      toast.success('Wedding updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await client.api.weddings[':id'].$delete({ param: { id: wedding.id } });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to hide this wedding');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weddings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
      toast.success('Wedding hidden. Nothing in Gmail was touched.');
      navigate('/weddings');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const slugPreview =
    form.partner1FirstName.trim() && form.partner2FirstName.trim()
      ? buildWeddingSlug(coupleShortName(form), form.weddingDate || null)
      : null;

  // The label the planner has right now, and whether editing the names here
  // could still move it. A picked label never follows the names — that is the
  // whole point of having picked it — so it is stated as fixed from the start
  // rather than only once the first inquiry goes out.
  const currentLabel = weddingLabelName(wedding);
  const labelFollowsSlug = !wedding.gmailLabelName;

  const busy = save.isPending || remove.isPending;

  if (confirmingDelete) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Hide {coupleShortName(wedding)}?</DialogTitle>
            {/* Worded as what it actually does. The planner's real question is
                "will this delete my emails", and the answer is no. */}
            <DialogDescription>
              This wedding and its inquiries disappear from VendorOps. Your mailbox is not touched:
              every message stays in Gmail, and the{' '}
              <code className="font-medium">{currentLabel}</code> label stays exactly where it
              is.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setConfirmingDelete(false)}>
              Keep it
            </Button>
            <Button variant="destructive" disabled={busy} onClick={() => remove.mutate()}>
              {remove.isPending ? 'Hiding…' : 'Hide this wedding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit wedding</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <CouplePhotoField
            weddingId={wedding.id}
            couple={wedding}
            hasPhoto={Boolean(wedding.photoKey)}
          />

          <WeddingFormFields value={form} onChange={setForm} idPrefix="edit" />

          <div className="space-y-2">
            <Label htmlFor="edit-status">Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v as WeddingStatus })}
            >
              <SelectTrigger id="edit-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEDDING_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {WEDDING_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* The slug rule, stated only where it bites. Before the first send
              it is a live preview of what you are choosing; after, it is a
              standing fact about the planner's mailbox. */}
          {labelIsLive ? (
            <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-xs text-muted-foreground">
              The Gmail label stays <code className="font-medium">{currentLabel}</code> — it's
              already in your mailbox. Renaming the couple here updates VendorOps, and leaves the
              label alone so your filed mail stays where you filed it.
            </p>
          ) : !labelFollowsSlug ? (
            <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-xs text-muted-foreground">
              Gmail label: <code className="font-medium">{currentLabel}</code>
              <br />
              You named this one yourself when you created the wedding, so it stays put no matter
              what you change here.
            </p>
          ) : (
            slugPreview && (
              <p className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-xs text-muted-foreground">
                Gmail label:{' '}
                <code className="font-medium">{labelNameForSlug(slugPreview, LABEL_PREFIX)}</code>
                <br />
                Nothing has been sent yet, so this still follows the name. It's fixed once the first
                inquiry goes out.
              </p>
            )
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            disabled={busy}
            onClick={() => setConfirmingDelete(true)}
          >
            Hide this wedding
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                !form.partner1FirstName.trim() || !form.partner2FirstName.trim() || busy
              }
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
